import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wifi, CheckCircle2, XCircle, Music2 } from "lucide-react";
import { api } from "@/lib/api";
import { StepShell, Field, TInput } from "./WizardUI";
import { BROADCAST_PROVIDERS, BROADCAST_FIELD_LABELS } from "./constants";

export default function Step4Broadcast({ saved, broadcast, onSave, onBack, onSkip, busy }) {
  const init = { ...(broadcast || {}), ...(saved || {}) };
  const [provider, setProvider] = useState(init.provider || "");
  const [f, setF] = useState({
    stream_url: init.stream_url || "", metadata_url: init.metadata_url || "",
    server_url: init.server_url || "", station_ref: init.station_ref || "",
    mount_point: init.mount_point || "", port: init.port || "",
    username: init.username || "", password: "", api_key: "",
  });
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const meta = BROADCAST_PROVIDERS.find((p) => p.v === provider);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const runTest = async () => {
    const url = f.metadata_url || f.stream_url || f.server_url;
    if (!url) { toast.error("Enter a stream or metadata URL first"); return; }
    setTesting(true); setTest(null);
    try {
      const { data } = await api.post("/onboarding/broadcast/test", { url });
      setTest(data);
      if (data.ok) toast.success("Connection working — metadata received");
    } catch (e) {
      setTest({ ok: false, error: e?.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  };

  const submit = () => {
    if (!provider) { onSkip(); return; }
    onSave({ provider, ...f });
  };

  return (
    <StepShell step={4} title="Broadcasting & metadata"
      subtitle="Connect your streaming or playout system so BroadcastFlow can read what's on air. You can skip this and connect later."
      onBack={onBack} onContinue={submit} onSkip={onSkip} busy={busy}
      continueLabel={provider === "not_ready" || !provider ? "Continue without connecting" : "Save & Continue"}>
      <Field label="Which system do you use?">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {BROADCAST_PROVIDERS.map((p) => (
            <button key={p.v} type="button" onClick={() => { setProvider(p.v); setTest(null); }}
              data-testid={`onb-provider-${p.v}`}
              className={`rounded-md border px-3 py-2.5 text-xs text-left transition ${
                provider === p.v ? "border-brand-amber/70 bg-brand-amber/10 text-brand-amber"
                                 : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"}`}>
              {p.l}
            </button>
          ))}
        </div>
      </Field>
      {meta && meta.fields.length > 0 && (
        <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            {meta.fields.map((k) => (
              <Field key={k} label={BROADCAST_FIELD_LABELS[k]} testid={`onb-bc-field-${k}`}>
                <TInput type={["password", "api_key"].includes(k) ? "password" : "text"}
                  value={f[k]} onChange={set(k)} autoComplete="new-password"
                  placeholder={["password", "api_key"].includes(k)
                    ? (broadcast?.[`has_${k === "api_key" ? "api_key" : "password"}`] ? "•••••• (saved — enter to replace)" : "")
                    : k.includes("url") ? "https://…" : ""} />
              </Field>
            ))}
          </div>
          <p className="text-[10px] text-ink-muted">Passwords and API keys are encrypted at rest and never shown again.</p>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={runTest} disabled={testing} data-testid="onb-bc-test-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-500/20 transition disabled:opacity-50">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />}
              Test Connection
            </button>
            {broadcast?.connected && !test && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" /> Connected
                {broadcast.last_metadata_at && ` · last metadata ${new Date(broadcast.last_metadata_at).toLocaleString()}`}
              </span>
            )}
          </div>
          {test && (
            <div data-testid="onb-bc-test-result"
              className={`rounded-md border px-3 py-2.5 text-xs ${test.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-rose-500/40 bg-rose-500/10 text-rose-200"}`}>
              {test.ok ? (
                <span className="inline-flex items-center gap-2">
                  <Music2 className="h-3.5 w-3.5" />
                  Now Playing: {[test.now_playing?.artist, test.now_playing?.title || test.now_playing?.song].filter(Boolean).join(" — ") || "metadata received"}
                </span>
              ) : (
                <span className="inline-flex items-start gap-2"><XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{test.error}</span>
              )}
            </div>
          )}
        </div>
      )}
    </StepShell>
  );
}
